/**
 * Error Fallback UI Component
 *
 * Displays user-friendly error messages with retry functionality.
 * Used by ErrorBoundary components to render error states.
 */

import { AlertCircle, RefreshCw, Home, Bug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export type ErrorType = "unknown" | "network" | "not_found" | "server" | "render";

export interface ErrorFallbackProps {
  /** The error that was caught */
  error: Error;
  /** Callback to reset the error boundary and retry */
  resetErrorBoundary?: () => void;
  /** Type of error for customized messaging */
  errorType?: ErrorType;
  /** Optional title override */
  title?: string;
  /** Optional message override */
  message?: string;
  /** Whether a retry is in progress */
  isRetrying?: boolean;
  /** Show home button for navigation errors */
  showHomeButton?: boolean;
  /** Show report button for error reporting */
  showReportButton?: boolean;
  /** Callback when report button is clicked */
  onReport?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Variant for different layouts */
  variant?: "default" | "compact" | "fullscreen";
}

/**
 * Get default error info based on error type
 */
function getErrorInfo(errorType: ErrorType): { title: string; message: string } {
  switch (errorType) {
    case "network":
      return {
        title: "Connection Problem",
        message:
          "We couldn't connect to our servers. Please check your internet connection and try again.",
      };
    case "not_found":
      return {
        title: "Page Not Found",
        message:
          "The page you're looking for doesn't exist or may have been moved.",
      };
    case "server":
      return {
        title: "Server Error",
        message:
          "Something went wrong on our end. Our team has been notified and is working on a fix.",
      };
    case "render":
      return {
        title: "Display Error",
        message:
          "We encountered a problem displaying this content. Try refreshing the page.",
      };
    case "unknown":
    default:
      return {
        title: "Something Went Wrong",
        message:
          "An unexpected error occurred. Please try again or contact support if the problem persists.",
      };
  }
}

/**
 * Detect error type from error object
 */
export function detectErrorType(error: Error): ErrorType {
  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  // Network errors
  if (
    message.includes("network") ||
    message.includes("fetch") ||
    message.includes("connection") ||
    name === "typeerror" && message.includes("failed to fetch")
  ) {
    return "network";
  }

  // Not found errors
  if (message.includes("404") || message.includes("not found")) {
    return "not_found";
  }

  // Server errors
  if (
    message.includes("500") ||
    message.includes("502") ||
    message.includes("503") ||
    message.includes("server error")
  ) {
    return "server";
  }

  return "unknown";
}

export function ErrorFallback({
  error,
  resetErrorBoundary,
  errorType,
  title,
  message,
  isRetrying = false,
  showHomeButton = true,
  showReportButton = false,
  onReport,
  className,
  variant = "default",
}: ErrorFallbackProps) {
  const detectedType = errorType ?? detectErrorType(error);
  const errorInfo = getErrorInfo(detectedType);
  const displayTitle = title ?? errorInfo.title;
  const displayMessage = message ?? errorInfo.message;

  const handleGoHome = () => {
    window.location.href = "/";
  };

  if (variant === "compact") {
    return (
      <div
        className={cn(
          "rounded-lg border border-destructive/50 bg-destructive/10 p-4",
          className
        )}
        role="alert"
        aria-live="assertive"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-destructive">
              {displayTitle}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">{displayMessage}</p>
            {resetErrorBoundary && (
              <Button
                variant="outline"
                size="sm"
                onClick={resetErrorBoundary}
                disabled={isRetrying}
                className="mt-3"
              >
                {isRetrying ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (variant === "fullscreen") {
    return (
      <div
        className={cn(
          "flex min-h-screen items-center justify-center bg-background p-4",
          className
        )}
        role="alert"
        aria-live="assertive"
      >
        <div className="w-full max-w-md text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <h1 className="mt-6 text-2xl font-bold text-foreground">
            {displayTitle}
          </h1>
          <p className="mt-3 text-muted-foreground">{displayMessage}</p>

          {import.meta.env.DEV && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                Technical Details
              </summary>
              <pre className="mt-2 overflow-auto rounded-lg bg-muted p-3 text-xs text-muted-foreground">
                {error.stack ?? error.message}
              </pre>
            </details>
          )}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            {resetErrorBoundary && (
              <Button
                onClick={resetErrorBoundary}
                disabled={isRetrying}
                className="gap-2"
              >
                {isRetrying ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </>
                )}
              </Button>
            )}
            {showHomeButton && (
              <Button variant="outline" onClick={handleGoHome} className="gap-2">
                <Home className="h-4 w-4" />
                Go Home
              </Button>
            )}
            {showReportButton && onReport && (
              <Button variant="ghost" onClick={onReport} className="gap-2">
                <Bug className="h-4 w-4" />
                Report Issue
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <Card
      className={cn("border-destructive/50", className)}
      role="alert"
      aria-live="assertive"
    >
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-5 w-5 text-destructive" />
          </div>
          <CardTitle className="text-destructive">{displayTitle}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pb-4">
        <p className="text-muted-foreground">{displayMessage}</p>

        {import.meta.env.DEV && (
          <details className="mt-4">
            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              Technical Details
            </summary>
            <pre className="mt-2 overflow-auto rounded-lg bg-muted p-3 text-xs text-muted-foreground max-h-40">
              {error.stack ?? error.message}
            </pre>
          </details>
        )}
      </CardContent>
      <CardFooter className="flex flex-wrap gap-3">
        {resetErrorBoundary && (
          <Button
            onClick={resetErrorBoundary}
            disabled={isRetrying}
            className="gap-2"
          >
            {isRetrying ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Retrying...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Try Again
              </>
            )}
          </Button>
        )}
        {showHomeButton && (
          <Button variant="outline" onClick={handleGoHome} className="gap-2">
            <Home className="h-4 w-4" />
            Go Home
          </Button>
        )}
        {showReportButton && onReport && (
          <Button variant="ghost" onClick={onReport} className="gap-2">
            <Bug className="h-4 w-4" />
            Report Issue
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
